import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { VendorApi } from '@mesa-labs/mesa-api/dist/apis/vendor';
import { types as api } from '@mesa-labs/mesa-api';

import { prepareHeaders, useApi } from './api';

const AUDIT_HISTORY_PAGE_SIZE = 100;

export const emailSequencesApi = createApi({
  reducerPath: 'emailSequencesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CONFIG.api.vendorUrl,
    prepareHeaders: async (headers) => await prepareHeaders(headers),
  }),
  tagTypes: ['EmailSequences', 'EmailSequence', 'EmailSequenceAuditLog', 'EmailTemplateRequiredConditions'],
  endpoints: (build) => ({
    getAllEmailSequences: build.query<api.IPagedResults<api.EmailSequenceResponse>, (api.ISortFilterParams & api.IPageFilterParams) | void>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getAllEmailSequences(params ?? undefined);
        return { data };
      },
      providesTags: ['EmailSequences'],
    }),

    getEmailSequence: build.query<api.EmailSequenceResponse | undefined, { id: string }>({
      async queryFn({ id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getEmailSequence(id);
        return { data };
      },
      providesTags: (result, error, { id }) => [{ type: 'EmailSequence' as const, id }],
    }),

    createEmailSequence: build.mutation<api.EmailSequenceResponse, api.CreateEmailSequenceRequest>({
      async queryFn(body) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.createEmailSequence(body);
        return { data };
      },
      invalidatesTags: ['EmailSequences', 'EmailSequenceAuditLog'],
    }),

    /** MESA-4976: atomic save of the template's fields and complete step list. */
    saveEmailSequence: build.mutation<api.EmailSequenceResponse, api.SaveEmailSequenceRequest & { id: string }>({
      async queryFn({ id, ...body }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.saveEmailSequence(id, body);
        return { data };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'EmailSequence' as const, id }, 'EmailSequences', 'EmailSequenceAuditLog'],
    }),

    deleteEmailSequence: build.mutation<void, { id: string }>({
      async queryFn({ id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        await vendorApi.deleteEmailSequence(id);
        return { data: undefined };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'EmailSequence' as const, id }, 'EmailSequences', 'EmailSequenceAuditLog'],
    }),

    cloneEmailSequence: build.mutation<api.EmailSequenceResponse, { id: string }>({
      async queryFn({ id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.cloneEmailSequence(id);
        return { data };
      },
      invalidatesTags: ['EmailSequences', 'EmailSequenceAuditLog'],
    }),

    /**
     * MESA-4976. Vendor sequence changes go through vendorsApi, which can't invalidate this API's
     * tags -- the audit log page refetches on mount instead.
     */
    getEmailSequenceAuditLog: build.query<api.IPagedResults<api.EmailSequenceAuditLogEntryResponse>, api.EmailSequenceAuditLogFilterParams | void>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getEmailSequenceAuditLog(params ?? undefined);
        return { data };
      },
      providesTags: ['EmailSequenceAuditLog'],
    }),

    getEmailSequenceAuditLogEntry: build.query<api.EmailSequenceAuditLogEntryDetailResponse | undefined, { id: string }>({
      async queryFn({ id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getEmailSequenceAuditLogEntry(id);
        return { data };
      },
    }),

    /**
     * The same sequence's entry just before this one -- what its diff compares against -- or null for
     * its first entry. The log has no "previous" lookup, so this walks the sequence's history (newest first).
     */
    getPreviousEmailSequenceAuditLogEntry: build.query<api.EmailSequenceAuditLogEntryDetailResponse | null, Pick<api.EmailSequenceAuditLogEntryResponse, 'id' | 'entityType' | 'entityId'>>({
      async queryFn({ id, entityType, entityId }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const fetchPage = (page: number) => vendorApi.getEmailSequenceAuditLog({
          entityType, entityId, page, limit: AUDIT_HISTORY_PAGE_SIZE,
        });
        const findPrevious = async (page: number): Promise<api.EmailSequenceAuditLogEntryResponse | undefined> => {
          const { data: entries } = await fetchPage(page);
          const index = entries.findIndex((entry) => entry.id === id);
          if (index === -1) {
            return entries.length < AUDIT_HISTORY_PAGE_SIZE ? undefined : await findPrevious(page + 1);
          }
          if (index < entries.length - 1) {
            return entries[index + 1];
          }
          return entries.length < AUDIT_HISTORY_PAGE_SIZE ? undefined : (await fetchPage(page + 1)).data[0];
        };

        const previous = await findPrevious(1);
        const data = (previous && await vendorApi.getEmailSequenceAuditLogEntry(previous.id)) ?? null;
        return { data };
      },
    }),

    /**
     * Carries each template's required conditions (MESA-4980), which ops can change at any time --
     * so nothing is kept once unused, and callers also pass `refetchOnMountOrArgChange`.
     */
    getEmailTemplateNames: build.query<api.GetEmailTemplateNamesResponse, void>({
      async queryFn() {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getEmailTemplateNames();
        return { data };
      },
      keepUnusedDataFor: 0,
      providesTags: ['EmailTemplateRequiredConditions'],
    }),

    /** MESA-4980: replaces the whole template x condition matrix in one call (audited as one entry). */
    putEmailTemplateRequiredConditions: build.mutation<api.GetEmailTemplateNamesResponse, api.PutEmailTemplateRequiredConditionsRequest>({
      async queryFn(body) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.putEmailTemplateRequiredConditions(body);
        return { data };
      },
      invalidatesTags: ['EmailTemplateRequiredConditions', 'EmailSequenceAuditLog'],
    }),

    getEmailTemplatePreview: build.query<api.PreviewEmailTemplateResponse, { templateName: string }>({
      async queryFn({ templateName }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getEmailTemplatePreview(templateName);
        return { data };
      },
    }),
  }),
});

export const {
  useGetAllEmailSequencesQuery,
  useGetEmailSequenceQuery,
  useCreateEmailSequenceMutation,
  useSaveEmailSequenceMutation,
  useDeleteEmailSequenceMutation,
  useCloneEmailSequenceMutation,
  useGetEmailSequenceAuditLogQuery,
  useGetEmailSequenceAuditLogEntryQuery,
  useGetPreviousEmailSequenceAuditLogEntryQuery,
  useGetEmailTemplateNamesQuery,
  usePutEmailTemplateRequiredConditionsMutation,
  useGetEmailTemplatePreviewQuery,
  useLazyGetEmailTemplatePreviewQuery,
} = emailSequencesApi;
